import { useContext, useEffect, useState } from 'react'
import { CartContext } from "../../store/context/CartContext";
import PopupService from '../../services/popup.service';
import UploadsService from '../../services/uploads.service';
import {
    Box,
    Grid,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Stack,
    Chip,
    Avatar,
    IconButton,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    ButtonBase,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import CloseIcon from '@mui/icons-material/Close';


const PopUp = () => {
    const { setTitleSeccion } = useContext(CartContext);
    const [popups, setPopups] = useState([]);
    const [title, setTitle] = useState('');
    const [link, setLink] = useState('');
    const [file, setFile] = useState(null);
    const [isActive, setIsActive] = useState(false);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
    const [imagePreviewSrc, setImagePreviewSrc] = useState('');
    const [imagePreviewTitle, setImagePreviewTitle] = useState('');


    const getAllPopups = async () => {
        try {
            const response = await PopupService.getAllPopups();
            setPopups(response || []);
        } catch (error) {
            console.error("Error fetching popups:", error);
        }
    }

    const handleUploadImage = async (file) => {
        if (!file) return null;

        const presign = await UploadsService.presignPopUpImage(
            file.type || "image/jpeg"
        );

        await UploadsService.putToPresigned(
            presign.url,
            file,
            presign.headers
        );

        return { imageKey: presign.key || '', imageUrl: presign.publicUrl };
    };

    const handleCreatePopup = async (e) => {
        e.preventDefault();

        if (!title.trim()) {
            alert('El título es obligatorio');
            return;
        }

        if (!file) {
            alert('Tenés que seleccionar una imagen');
            return;
        }

        try {
            // 2 -----> SUBO a S3
            const uploadResult = await handleUploadImage(file);

            if (!uploadResult) {
                throw new Error('No se pudo subir la imagen');
            }

            const { imageKey, imageUrl } = uploadResult;

            const payload = {
                title,
                imageKey,
                imageUrl,
                isActive,
                link,
                deleted: false,
            };

            await PopupService.createPopup(payload);
            setTitle('');
            setFile(null);
            setIsActive(false);
            setPreview(null);
            setLink('');

            await getAllPopups();
        } catch (error) {
            console.error("Error creating popup:", error);
        }
    }

    // 4) Toggle status
    const handleToggleStatus = async (popup) => {
        try {
            // Lista de popups activos actualmente
            const activePopups = popups.filter((p) => p.isActive);

            // Caso A: queremos ACTIVAR este popup (ahora está inactivo)
            if (!popup.isActive) {
                const alreadyActive = activePopups.find((p) => p.id !== popup.id);

                if (alreadyActive) {
                    const confirmChange = window.confirm(
                        `Ya hay un popup activo: "${alreadyActive.title}".\n\n` +
                        `¿Querés desactivarlo y activar este nuevo?`
                    );

                    if (!confirmChange) {
                        return;
                    }

                    // 1) Desactivar el que estaba activo
                    await PopupService.updateStatusPopup(alreadyActive.id);

                    // 2) Activar el nuevo
                    await PopupService.updateStatusPopup(popup.id);

                    await getAllPopups();
                    return;
                }

                // No había ninguno activo → simplemente activamos este
                await PopupService.updateStatusPopup(popup.id);
                await getAllPopups();
                return;
            }

            // Caso B: estaba ACTIVO y queremos DESACTIVARLO
            await PopupService.updateStatusPopup(popup.id);
            await getAllPopups();
        } catch (error) {
            console.error('Error toggling popup status:', error);
            alert('No se pudo cambiar el estado del popup');
        }
    };

    // 5) Eliminar popup
    const handleDelete = async (id) => {
        const confirmDelete = window.confirm('¿Seguro que querés eliminar este popup?');
        if (!confirmDelete) return;

        try {
            await PopupService.deletePopup(id);
            await getAllPopups();
        } catch (error) {
            console.error('Error deleting popup:', error);
            alert('No se pudo eliminar el popup');
        }
    };

    // 7) Manejo de archivo + preview
    const handleFileChange = (e) => {
        const selected = e.target.files[0];
        setFile(selected || null);

        if (selected) {
            const url = URL.createObjectURL(selected);
            setPreview(url);
        } else {
            setPreview(null);
        }
    };


    const handleOpenImagePreview = (popup) => {
        if (!popup.imageUrl) return;
        setImagePreviewSrc(popup.imageUrl);
        setImagePreviewTitle(popup.title || '');
        setImagePreviewOpen(true);
    };

    const handleCloseImagePreview = () => {
        setImagePreviewOpen(false);
        setImagePreviewSrc('');
        setImagePreviewTitle('');
    };

    useEffect(() => {
        setTitleSeccion('Pop Ups');
        getAllPopups();

        return () => setTitleSeccion('');
    }, []);


    return (
        <Box sx={{
            p: 3,
            maxWidth: '100vw',
            boxSizing: 'border-box',
            overflowX: 'hidden', // evita scroll horizontal
        }}>
            {/* Header */}
            <Box
                sx={{
                    mb: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    overflowX: 'hidden', // evita scroll horizontal

                }}
            >
                <Box>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                        Pop Ups
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Creá, activá/desactivá y administrá los popups que se muestran en la app.
                    </Typography>
                </Box>

                <Chip
                    label={
                        popups.length === 0
                            ? 'Sin popups'
                            : `${popups.length} ${popups.length === 1 ? 'popup' : 'popups'}`
                    }
                    color="primary"
                    variant="outlined"
                />
            </Box>

            <Grid container spacing={3}>
                {/* Columna izquierda: formulario */}
                <Grid item xs={12} md={4}>
                    <Card elevation={4}>
                        <CardHeader
                            title="Nuevo popup"
                            subheader="Subí una imagen y añadí un título para mostrar en la app."
                        />
                        <Divider />
                        <CardContent>
                            <Stack spacing={2}>
                                {/* Título */}
                                <TextField
                                    label="Título"
                                    placeholder="Ej: Recordá hidratarte 💧"
                                    size="small"
                                    fullWidth
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                                {/* Link */}
                                <TextField
                                    label="Link (opcional)"
                                    size="small"
                                    fullWidth
                                    value={link}
                                    onChange={(e) => setLink(e.target.value)}
                                />


                                {/* Imagen */}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                        Imagen
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        size="small"
                                        fullWidth
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Seleccionar imagen
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ mt: 0.5, display: 'block' }}
                                    >
                                        Formato recomendado: JPG o PNG, proporción 16:9.
                                    </Typography>
                                </Box>

                                {/* Preview */}
                                {preview && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">
                                            Preview
                                        </Typography>
                                        <Box
                                            sx={{
                                                mt: 1,
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={preview}
                                                alt="Preview popup"
                                                aspe
                                                sx={{ width: '100%', objectFit: 'cover' }}
                                            />
                                        </Box>
                                    </Box>
                                )}

                            </Stack>
                        </CardContent>
                        <CardActions sx={{ p: 2, pt: 0 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={handleCreatePopup}
                                disabled={loading}
                            >
                                {loading ? 'Creando popup...' : 'Crear popup'}
                            </Button>
                        </CardActions>
                    </Card>
                </Grid>

                {/* Columna derecha: listado */}
                <Grid item xs={12} md={8}>
                    <Card elevation={4}>
                        <CardHeader
                            title="Popups creados"
                            subheader={
                                popups.length === 0
                                    ? 'Todavía no creaste ningún popup.'
                                    : 'Administrá el estado y eliminación de tus popups.'
                            }
                        />
                        <Divider />
                        <CardContent>
                            {popups.length === 0 ? (
                                <Box
                                    sx={{
                                        py: 4,
                                        textAlign: 'center',
                                        color: 'text.secondary',
                                    }}
                                >
                                    <Typography variant="body2">
                                        Creá tu primer popup desde el formulario de la izquierda.
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack spacing={1.5}>
                                    {popups.map((popup) => (
                                        <Card
                                            key={popup.id}
                                            variant="outlined"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                p: 1.5,
                                                gap: 2,
                                            }}
                                        >
                                            {/* Imagen */}

                                            <ButtonBase
                                                onClick={() => handleOpenImagePreview(popup)}
                                                sx={{
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <Avatar
                                                    variant="rounded"
                                                    src={popup.imageUrl || undefined}
                                                    alt={popup.title}
                                                    sx={{ width: 72, height: 72, borderRadius: 2 }}
                                                >
                                                    {!popup.imageUrl && popup.title?.[0]?.toUpperCase()}
                                                </Avatar>
                                            </ButtonBase>


                                            {/* Info */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '70%' }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            noWrap
                                                            sx={{ fontWeight: 500, maxWidth: '70%' }}
                                                        >
                                                            {popup.title}
                                                        </Typography>

                                                        <Typography
                                                            variant="subtitle1"
                                                            noWrap
                                                            sx={{ fontWeight: 500, maxWidth: '100%', cursor: 'pointer' }}
                                                            component="a"
                                                            href={popup?.link || '#'}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {popup?.link}
                                                        </Typography>
                                                    </Box>

                                                    <Chip
                                                        size="small"
                                                        label={popup.isActive ? 'Activo' : 'Inactivo'}
                                                        color={popup.isActive ? 'success' : 'default'}
                                                        variant={popup.isActive ? 'filled' : 'outlined'}
                                                    />
                                                </Box>

                                            </Box>

                                            {/* Acciones */}
                                            <Stack direction="row" spacing={1}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color={popup.isActive ? 'warning' : 'success'}
                                                    startIcon={<PowerSettingsNewIcon fontSize="small" />}
                                                    onClick={() => handleToggleStatus(popup)}
                                                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                                >
                                                    {popup.isActive ? 'Desactivar' : 'Activar'}
                                                </Button>

                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDelete(popup.id)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </Card>
                                    ))}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>


            <Dialog
                open={imagePreviewOpen}
                onClose={handleCloseImagePreview}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        pb: 1,
                        pr: 6,
                        position: 'relative'
                    }}
                >
                    {imagePreviewTitle || 'Vista previa'}

                    <IconButton
                        aria-label="close"
                        onClick={handleCloseImagePreview}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {imagePreviewSrc && (
                        <Box
                            component="img"
                            src={imagePreviewSrc}
                            alt={imagePreviewTitle}
                            sx={{
                                width: '100%',
                                borderRadius: 2,
                                display: 'block',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

        </Box>
    );

}

export default PopUp